import getJsonLdScriptId from "@insite/client-framework/Common/Utilities/getJsonLdScriptId";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import prepareBreadcrumbStructuredPageData from "@insite/client-framework/Common/Utilities/prepareBreadcrumbStructuredPageData";
import setPageMetadata from "@insite/client-framework/Common/Utilities/setPageMetadata";
import { trackPageChange } from "@insite/client-framework/Common/Utilities/tracking";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasDeferredApiContext, withCallDeferredApi } from "@insite/client-framework/Components/DeferredApiContext";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import { redirectTo, setStatusCode } from "@insite/client-framework/ServerSideRendering";
import { getContentByVersionPath } from "@insite/client-framework/Services/ContentService";
import { getUnitListPrice, getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import {
    getSearchDataModeActive,
    getSelectedCategoryPath,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import {
    getAlternateLanguageUrls,
    getCanonicalUrlByPath,
    getCurrentPage,
    getLocation,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import clearProducts from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearProducts";
import displayProducts from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import loadProductsPricingAndInventory from "@insite/client-framework/Store/Pages/ProductList/Handlers/LoadProductsPricingAndInventory";
import prepareProductRecs from "@insite/client-framework/Store/Pages/ProductList/Handlers/PrepareProductRecs";
import sendOdpEvents from "@insite/client-framework/Store/Pages/ProductList/Handlers/SendOdpEvents";
import translate from "@insite/client-framework/Translate";
import { HideFooter, HideHeader, OpenGraphImage, OpenGraphTitle } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import ProductCompareFlyOut from "@insite/content-library/Components/ProductCompareFlyOut";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getPrevCurrentNextQueryString } from "@insite/mobius/utilities/linkElementsManipulation";
import loadCatFaqs from "@tCentro/Handlers/CENLoadCatFaqs";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

declare global {
    interface Window {
        dataLayer: Record<string, any>[];
    }
}

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);

    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        getSelectedCategoryPath(state) ||
        (location.pathname === getContentByVersionPath
            ? parseQueryString<{ categoryPath: string }>(location.search).categoryPath
            : "") ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const {
        pages: {
            productList: {
                isLoading,
                productFilters,
                parameter,
                isSearchPage,
                filterQuery,
                productInfosByProductId,
                isCanceled,
            },
        },
    } = state;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    const productsDataView = getProductsDataView(state, parameter);
    const page = getCurrentPage(state);
    const category = getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value;
    const catFaq = state?.t_components?.catFaq;
    return {
        isLoading,
        stockedItemsOnly: productFilters.stockedItemsOnly,
        previouslyPurchasedProducts: productFilters.previouslyPurchasedProducts,
        query: productFilters.query,
        productsDataView,
        firstProductDetailPath: productsDataView.value
            ? productInfosByProductId[productsDataView.value[0]?.id]?.productDetailPath
            : undefined,
        filterQuery,
        isSearchPage,
        search: location.search,
        path,
        breadcrumbLinks: state.components.breadcrumbs.links,
        productListCatalogPage: getCatalogPageStateByPath(state, path).value,
        websiteName: state.context.website.name,
        pages: state.pages,
        location: getLocation(state),
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        websiteSettings: getSettingsCollection(state).websiteSettings,
        searchDataModeActive: getSearchDataModeActive(state),
        searchPath: getSettingsCollection(state).searchSettings.searchPath,
        alternateLanguageUrls: getAlternateLanguageUrls(state, page.id),
        page,
        deferredApis: state.context.deferredApiCalls,
        productInfosByProductId,
        parameter,
        unfilteredApiParameter: state.pages.productList.unfilteredApiParameter,
        canonicalUrl: getCanonicalUrlByPath(state, path),
        enableServerSideRendering: getSettingsCollection(state).websiteSettings.enableServerSideRendering,
        isCanceled,
        catFaq
    };
};

const mapDispatchToProps = {
    clearAllProductFilters,
    displayProducts,
    setBreadcrumbs,
    clearProducts,
    loadProductsPricingAndInventory,
    sendOdpEvents,
    prepareProductRecs,
    loadCatFaqs
};

export interface ProductListPageStyles {
    productListPageContainer?: InjectableCss;
}

export const ProductListPageStyles: ProductListPageStyles = {
    productListPageContainer: {
        css: css`
            .needHelpSection {
                div[class^="GridOffset"] {
                    div[class^="GridItemStyle"] {
                        padding-bottom: 0;
                        &:last-child {
                            padding: 0;
                            display: flex;
                            align-items: flex-end;
                        }
                    }
                }
            }
        `,
    },
};

const styles = ProductListPageStyles;

export const ProductListPageDataContext = React.createContext<{ ref?: React.RefObject<HTMLSpanElement | null> }>({
    ref: undefined,
});

type Props = HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    PageProps &
    HasDeferredApiContext;

type CatFaqs = NonNullable<Props["catFaq"]>["faqs"];

class ProductListPage extends React.Component<
    Props,
    { sentEventFor?: string; sentViewItemListFor?: string }
> {
    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    afterFilters = React.createRef<HTMLSpanElement | null>();

    // private loadCatFaqsIfNeeded = () => {
    //     const categoryId = this.props.productListCatalogPage?.categoryId;
    //     console.log("#####################", categoryId, this.props.productListCatalogPage)
    //     if (categoryId && !this.props.catFaq?.isLoading && this.props.catFaq?.categoryId !== categoryId) {
    //         this.props.loadCatFaqs({ categoryId });
    //     }
    // };

    loadProducts() {
        const { displayProducts, path, search } = this.props;

        const parameter = {
            path,
            queryString: search,
            isSearch: this.isSearchPath(),
            relevancy: true,
            onException: this.redirectToNotFound,
        };

        displayProducts(parameter);
    }

    private redirectToNotFound = () => {
        if (IS_SERVER_SIDE) {
            setStatusCode(404);
            redirectTo("/NotFoundErrorPage");
            return;
        }

        this.props.history.replace("/NotFoundErrorPage");
    }

    isSearchPath() {
        return !!this.props.path.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`);
    }

    getCanonicalUrl = (path: string) => {
        if (!path) return path;
        const lowerPath = path?.toLowerCase();

        if (lowerPath.includes("/catalog/all")) return path;
        if (!lowerPath.startsWith("/brands/")) return path;

        const parts = path?.split("/");

        const newPath = "/" + parts?.slice(3).join("/");

        return newPath;
    };

    UNSAFE_componentWillMount(): void {
        const categoryId = this.props.productListCatalogPage?.categoryId ?? this.props.category?.id.split(":")[0];
        const catFaq = this.props.catFaq;

        if (
            IS_SERVER_SIDE &&
            categoryId &&
            !catFaq?.isLoading &&
            catFaq?.categoryId !== categoryId
        ) {
            this.props.loadCatFaqs({ categoryId });
        }

        if (!IS_SERVER_SIDE) {
            // cleanup state for future redirect captures
            window?.localStorage?.removeItem("searchTermRedirectUrl");
        }

        const { productsDataView, breadcrumbLinks } = this.props;
        if (
            ((!productsDataView.value && !productsDataView.isLoading) || this.props.isCanceled) &&
            !(IS_SERVER_SIDE && this.props.deferredApis.catalog > 0)
        ) {
            this.loadProducts();
        } else if (!IS_SERVER_SIDE && productsDataView.value) {
            if (this.checkSearchTermRedirectUrl()) {
                return;
            }
        } else if (this.props.isSearchPage) {
            this.redirectToProductDetailIfExactMatch();
        }

        this.props.callDeferredApis({
            pricingHandler: () => {
                this.props.loadProductsPricingAndInventory({
                    products: this.props.productsDataView.value,
                    productInfosByProductId: this.props.productInfosByProductId,
                });
            },
            inventoryHandler: () => {
                this.props.loadProductsPricingAndInventory({
                    products: this.props.productsDataView.value,
                    productInfosByProductId: this.props.productInfosByProductId,
                });
            },
        });

        if (this.props.productListCatalogPage) {
            this.setMetadata();
            if (
                breadcrumbLinks
                    ?.slice(1)
                    ?.map(o => o.children?.toString())
                    .join(",") !==
                this.props.productListCatalogPage.breadCrumbs
                    ?.slice(1)
                    ?.map(o => o.text)
                    .join(",") ||
                (breadcrumbLinks?.length && breadcrumbLinks[0].children !== translate("Home_Breadcrumb"))
            ) {
                this.setProductListBreadcrumbs();
            }
        } else if (this.props.isSearchPage) {
            this.setSearchPageMetadata(translate("Search Results"));
            if (!this.props.breadcrumbLinks) {
                this.setSearchBreadcrumbs();
            }
        }
        this.sendOdpEventsIfNeeded();
    }

    componentDidMount(): void {
        if (this.props.enableServerSideRendering) {
            this.props.prepareProductRecs({
                isSearch: this.isSearchPath(),
                catalogPage: this.props.productListCatalogPage,
                products: this.props.productsDataView.value,
            });
        }

        this.pushViewItemListDataLayer();
    }

    componentWillUnmount(): void {
        this.props.clearProducts();
        this.props.setBreadcrumbs({ links: undefined });
    }

    componentDidUpdate(prevProps: Props): void {
        // this.loadCatFaqsIfNeeded();

        const {
            isLoading,
            filterQuery,
            location: { pathname, search },
            productListCatalogPage,
            searchDataModeActive,
        } = this.props;

        if (productListCatalogPage) {
            this.setMetadata();
            trackPageChange();
            if (
                productListCatalogPage.categoryId !== prevProps.productListCatalogPage?.categoryId ||
                !this.props.breadcrumbLinks
            ) {
                this.setProductListBreadcrumbs();
            }
        } else if (this.props.isSearchPage && search && search.length > 0) {
            // For pages that do not have a productListCatalogPage, mainly the Search Page
            this.setSearchPageMetadata(translate("Search Results"));
            trackPageChange();
            if (this.props.query !== prevProps.query || !this.props.breadcrumbLinks) {
                this.setSearchBreadcrumbs();
            }
        }
        if (this.props.query !== prevProps.query) {
            window?.localStorage?.removeItem("searchTermRedirectUrl");
        }
        // handle the query string change requests initiated by the filtering widget setQueryFilter calls
        // Make sure we are fully loaded, since a loading product list will change the filteQuery to match the current pathname/search
        // This can trigger a loop with the pathname/search updating and the filterQuery not updated till the productList is loaded
        if (!isLoading && filterQuery !== prevProps.filterQuery) {
            const currentUrl = `${pathname}${search}`;
            const newUrl = filterQuery ? `${pathname}?${filterQuery}` : pathname;
            if (newUrl !== currentUrl) {
                this.props.history.push(newUrl);
            }
            return;
        }

        if (!isLoading && this.props.productsDataView.value && pathname === prevProps.location.pathname) {
            if (this.checkSearchTermRedirectUrl()) {
                return;
            }
            this.redirectToProductDetailIfExactMatch();
        }

        const isStartingProductsRequest =
            !isLoading &&
            (search !== prevProps.search || this.props.path !== prevProps.path) &&
            search?.indexOf("criteria=") === -1;
        if (isStartingProductsRequest) {
            if (this.props.path !== prevProps.path) {
                this.props.clearAllProductFilters({
                    removeAll: true,
                });
            }
            this.loadProducts();
        }

        if (searchDataModeActive !== prevProps.searchDataModeActive) {
            // refresh the search results when search data mode has been toggled in the cms shell
            this.loadProducts();
        }

        if (!isStartingProductsRequest) {
            this.pushViewItemListDataLayer();
        }
        this.sendOdpEventsIfNeeded();
    }

    private slugifyDataLayerValue = (value?: string) => {
        return (value || "")
            .toLowerCase()
            .trim()
            .replace(/\s+/g, "_")
            .replace(/[^a-z0-9_]/g, "");
    };

    private getViewItemListId = () => {
        const { path, productsDataView, category } = this.props;
        const lowerPath = path?.toLowerCase() || "";

        if (lowerPath.startsWith("/brands/")) {
            const brandName = productsDataView?.value?.[0]?.brand?.name;
            const brandPath = lowerPath.split("/").filter(Boolean)[1];
            const brandValue = brandName || brandPath;

            return brandValue ? `brand_${this.slugifyDataLayerValue(brandValue)}` : "brand";
        }

        if (lowerPath.startsWith("/search")) {
            return `search_results`
        }

        const categoryName = category?.shortDescription || (category as any)?.description || category?.name;
        const categoryValue = categoryName || path?.split("/").filter(Boolean).pop();

        return categoryValue ? `category_${this.slugifyDataLayerValue(categoryValue)}` : "product_list";
    };

    private getViewItemListName = () => {
        const { path, productsDataView, category } = this.props;
        const lowerPath = path?.toLowerCase() || "";

        if (lowerPath.startsWith("/brands/")) {
            const brandName = productsDataView?.value?.[0]?.brand?.name;
            const brandPath = path?.split("/").filter(Boolean)[1];
            const fallbackBrandName = brandPath
                ? brandPath.replace(/[-_]+/g, " ").replace(/\b\w/g, character => character.toUpperCase())
                : "";

            return `Brand: ${brandName || fallbackBrandName}`;
        }

        if (lowerPath.startsWith("/search")) {
            return `Search: ${(productsDataView as any)?.originalQuery}`
        }

        const categoryName = category?.shortDescription || (category as any)?.description || category?.name;
        const categoryPath = path?.split("/").filter(Boolean).pop();
        const fallbackCategoryName = categoryPath
            ? categoryPath.replace(/[-_]+/g, " ").replace(/\b\w/g, character => character.toUpperCase())
            : "";

        return `Category: ${categoryName || fallbackCategoryName}`;
    };

    private getViewItemCategoryProperties = () => {
        const { breadcrumbLinks, category } = this.props;

        const EXCLUDED_BREADCRUMBS = ["Search Results", "Shop All Products"];

        const hasExcludedBreadcrumb = (breadcrumbLinks: any) =>
            breadcrumbLinks?.some(
                (item: any) =>
                    typeof item.children === "string" &&
                    EXCLUDED_BREADCRUMBS.some((text) =>
                        (item.children as string).includes(text)
                    )
            );

        if (hasExcludedBreadcrumb(breadcrumbLinks)) {
            return false;
        }

        const breadcrumbs = breadcrumbLinks || [];
        let categoryNames: string[] = [];

        if (breadcrumbs.length > 0) {
            const breadcrumbNames = breadcrumbs
                .map((breadcrumb: any) => (typeof breadcrumb?.children === "string" ? breadcrumb.children.trim() : ""))
                .filter(Boolean);

            const brandsIndex = breadcrumbNames.findIndex(name => name.toLowerCase() === "brands");

            if (brandsIndex > 0) {
                categoryNames = [breadcrumbNames[brandsIndex - 1]];
            } else {
                categoryNames = breadcrumbNames.filter(
                    name => name.toLowerCase() !== translate("Home_Breadcrumb").toLowerCase(),
                );
            }
        }

        if (categoryNames.length === 0) {
            const categoryName = category?.shortDescription || (category as any)?.description || category?.name;
            if (categoryName) {
                categoryNames = [categoryName];
            }
        }

        return categoryNames.slice(0, 5).reduce<Record<string, string>>((categoryProperties, name, index) => {
            categoryProperties[index === 0 ? "item_category" : `item_category${index + 1}`] = name;
            return categoryProperties;
        }, {});
    };

    private getViewItemListEventKey = () => {
        const { path, search, productsDataView } = this.props;
        const productIds = (productsDataView?.value || [])
            .map((product: any) => product?.productNumber || product?.id || "")
            .join("|");

        return `${path}${search}|${productIds}`;
    };

    private pushViewItemListDataLayer = () => {
        if (typeof window === "undefined") {
            return;
        }


        const products = this.props.productsDataView?.value;
        if (!products?.length) {
            return;
        }

        // Do not fire a list impression when the PLP is immediately redirecting
        // to a single product detail page.
        const redirectingToProduct =
            this.props.productsDataView.exactMatch ||
            (this.props.productsDataView.searchTermRedirectUrl &&
                !window?.localStorage?.getItem("searchTermRedirectUrl"));

        if (redirectingToProduct) {
            return;
        }

        const eventKey = this.getViewItemListEventKey();
        if (eventKey === this.state.sentViewItemListFor) {
            return;
        }

        const itemCategoryProperties = this.getViewItemCategoryProperties();
        const itemListId = this.getViewItemListId();
        const itemListName = this.getViewItemListName();
        const pagination = this.props.productsDataView.pagination;
        const currentPage = pagination?.currentPage || 1;
        const requestedPage = parseQueryString<{ page?: string }>(this.props.search).page;
        if (requestedPage && Number(requestedPage) !== currentPage) {
            return;
        }
        const pageSize = pagination?.pageSize || products.length || 1;
        const offset = (currentPage - 1) * pageSize;
        const productInfos = products.map((product: any) => this.props.productInfosByProductId[product.id]);
        if (productInfos.some(productInfo => typeof productInfo?.pricing?.actualPrice !== "number")) {
            return;
        }

        const items = products.map((product: any, index: number) => {
            const productInfo: any = this.props.productInfosByProductId[product.id];
            const pricing: any = productInfo.pricing;
            const realTimePrice = pricing?.actualPrice;
            const realQty = productInfo?.qtyOrdered || 1;

            return {
                item_id: product?.productNumber || product?.id || "",
                item_name: product?.productTitle || product?.content?.pageTitle || "",
                index: offset + index,
                item_brand: product?.brand?.name || "",
                ...itemCategoryProperties,
                item_list_id: itemListId,
                item_list_name: itemListName,
                price: realTimePrice,
                quantity: realQty,
            }
        });

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
            event: "view_item_list",
            ecommerce: {
                item_list_id: itemListId,
                item_list_name: itemListName,
                items,
            },
        });

        this.setState({ sentViewItemListFor: eventKey });
    };

    sendOdpEventsIfNeeded = () => {
        if (IS_SERVER_SIDE) {
            return;
        }

        const { parameter, sendOdpEvents, productListCatalogPage, productsDataView, unfilteredApiParameter } =
            this.props;

        if (!unfilteredApiParameter) {
            return;
        }

        const nextParameter = JSON.stringify(parameter);
        if (nextParameter !== this.state.sentEventFor && productsDataView?.value) {
            this.setState({
                sentEventFor: nextParameter,
            });

            let lastParameter = this.state.sentEventFor ? JSON.parse(this.state.sentEventFor) : undefined;
            sendOdpEvents({
                catalogPage: productListCatalogPage,
                lastParameter,
            });
        }
    };
    checkSearchTermRedirectUrl() {
        if (!window || !this.props.productsDataView.value) {
            return false;
        }

        let searchTermRedirectUrl = this.props.productsDataView.searchTermRedirectUrl;
        if (!searchTermRedirectUrl) {
            return false;
        }

        const startsWithProtocol = searchTermRedirectUrl.lastIndexOf("http", 0) === 0;
        if (!startsWithProtocol && searchTermRedirectUrl[0] !== "/") {
            searchTermRedirectUrl = `/${searchTermRedirectUrl}`;
        }

        const locationPath = startsWithProtocol
            ? window.location.toString()
            : `${window.location.pathname}${window.location.search}`;

        if (locationPath === searchTermRedirectUrl) {
            window.localStorage.setItem("searchTermRedirectUrl", searchTermRedirectUrl);
            return false;
        }

        if (window.localStorage.getItem("searchTermRedirectUrl") === searchTermRedirectUrl) {
            return false;
        }

        if (startsWithProtocol) {
            window.location.replace(searchTermRedirectUrl);
        } else {
            this.props.history.replace(searchTermRedirectUrl);
        }

        window.localStorage.setItem("searchTermRedirectUrl", searchTermRedirectUrl);

        return true;
    }

    redirectToProductDetailIfExactMatch() {
        if (!this.props.productsDataView.value) {
            return;
        }

        const {
            location: { search },
            firstProductDetailPath,
        } = this.props;

        const { exactMatch, value: products } = this.props.productsDataView;
        if (exactMatch && products.length) {
            let productDetailUrl = firstProductDetailPath ?? products[0].canonicalUrl;
            const parsedQuery = parseQueryString<{ criteria: string; query: string }>(search);
            const query = parsedQuery.criteria || parsedQuery.query;
            if (!query) {
                return;
            }
            if (productDetailUrl.indexOf("?") !== -1) {
                productDetailUrl += `&criteria=${encodeURIComponent(query)}`;
            } else {
                productDetailUrl += `?criteria=${encodeURIComponent(query)}`;
            }

            if (IS_SERVER_SIDE) {
                setStatusCode(302);
                redirectTo(productDetailUrl);
            } else {
                this.props.history.replace(productDetailUrl);
            }
        }
    }

    getQueryStrings() {
        if (this.props.productsDataView.value && this.props.productsDataView.pagination) {
            const { prevPageUri, nextPageUri, currentPage } = this.props.productsDataView.pagination;
            const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString(
                prevPageUri,
                nextPageUri,
                currentPage,
                this.props.filterQuery ?? "",
            );
            return { prevQueryString, currentQueryString, nextQueryString };
        }

        const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString("", "", 1, "");
        return { prevQueryString, currentQueryString, nextQueryString };
    }

    private getAbsoluteUrl(path?: string) {
        if (!path) {
            return "";
        }

        if (path.startsWith("http://") || path.startsWith("https://")) {
            return path;
        }

        const url = IS_SERVER_SIDE ? getUrl() : window.location;

        if (!url) {
            return path;
        }

        const origin = `${url.protocol}//${url.host}`;

        return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
    }

    // Real current page, narrowed the same way getQueryStrings() does (checking
    // .value first, since one branch of productsDataView's union type has no
    // pagination field at all).
    private getCurrentPageNumber(): number {
        const { productsDataView } = this.props;
        const pagination = productsDataView.value ? productsDataView.pagination : undefined;
        return pagination && pagination.currentPage ? pagination.currentPage : 1;
    }

    // Subcategories don't paginate — only emit this ItemList on page 1,
    // otherwise it gets duplicated (with identical positions) on every page.
    private getSubcategoryItemListStructuredData() {
        const { productListCatalogPage, category } = this.props;
        const subCategories =
            productListCatalogPage?.properties?.subCategories &&
            JSON.parse(decodeURI(productListCatalogPage.properties.subCategories));

        if (!productListCatalogPage || !category || !subCategories || subCategories.length === 0) {
            return null;
        }

        if (this.getCurrentPageNumber() !== 1) {
            return null;
        }

        return {
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: subCategories.map((subcategory: any, index: number) => ({
                "@type": "ListItem",
                position: index + 1,
                name: subcategory.name,
                url: this.getAbsoluteUrl(
                    subcategory.customProperties?.find((property: any) => property.name === "path")?.value,
                ),
            })),
        };
    }

    // position is the ABSOLUTE rank across the whole result set:
    // position = (currentPage - 1) * pageSize + index + 1 — otherwise every
    // page restarts numbering at 1, which is what was happening before this
    // offset was added.
    // VERIFY: if productsDataView.pagination exposes an explicit page-size
    // field (commonly `pageSize` on Insite's pagination model), swap it in
    // below instead of the products.length approximation.
    private getProductsItemListStructuredData() {
        const { productsDataView } = this.props;

        if (!productsDataView?.value?.length) {
            return null;
        }

        const pagination = productsDataView.value ? productsDataView.pagination : undefined;
        const currentPage = pagination && pagination.currentPage ? pagination.currentPage : 1;
        const pageSize = pagination && pagination.pageSize ? pagination.pageSize : productsDataView.value.length || 1;
        const offset = (currentPage - 1) * pageSize;

        return {
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: productsDataView.value.map((product: any, index: number) => ({
                "@type": "ListItem",
                position: offset + index + 1,
                url: this.getAbsoluteUrl(product.canonicalUrl),
            })),
        };
    }

    private getFaqStructuredData() {
        const catFaq = this.props?.catFaq;
        const faqs = catFaq?.faqs as CatFaqs | undefined;

        if (catFaq?.isLoading || !Array.isArray(faqs) || faqs.length === 0) {
            return null;
        }

        const validFaqs = faqs.filter(faq => faq?.question?.trim() && faq?.answer?.trim());

        if (validFaqs.length === 0) {
            return null;
        }

        return {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: validFaqs.map(faq => ({
                "@type": "Question",
                name: faq.question.trim(),
                acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.answer.trim(),
                },
            })),
        };
    }

    setMetadata() {
        const { productListCatalogPage, websiteName, location, websiteSettings, category } = this.props;
        if (!productListCatalogPage) {
            return;
        }

        const {
            metaDescription,
            metaKeywords,
            openGraphImage,
            openGraphTitle,
            openGraphUrl,
            title,
            primaryImagePath,
            canonicalPath,
            alternateLanguageUrls,
            breadCrumbs,
        } = productListCatalogPage;

        const normalizeTitle = (value?: string) =>
            value
                ? value
                    .normalize()
                    .replace(/\u00A0/g, " ")
                    .replace(/\s+/g, " ")
                    .trim()
                : "";

        const isMainCategory = breadCrumbs && breadCrumbs.length <= 2;
        const isSubCategory = breadCrumbs && breadCrumbs.length === 3;
        /* */
        const isBrandPage = breadCrumbs && breadCrumbs[1]?.text.toLowerCase() === "brands";
        const mainCategory = breadCrumbs && breadCrumbs[1]?.text;
        const lastCategory = breadCrumbs && breadCrumbs[breadCrumbs.length - 1]?.text;
        const secondLastCategory = (breadCrumbs && breadCrumbs[breadCrumbs.length - 2]?.text) || "";
        let customTitle;
        let customMetaDescription;

        let finalMetaTitle = title;
        if (normalizeTitle(title) === normalizeTitle(category?.shortDescription)) {
            finalMetaTitle = "";
        }

        // Prevent using title as metaDescription if metaDescription is empty or matches title
        let finalMetaDescription = metaDescription;
        if (category?.name?.trim() === metaDescription?.trim()) {
            finalMetaDescription = "";
        }

        if (isBrandPage) {
            customTitle = `${secondLastCategory} ${lastCategory} Products Online | ${websiteName}`;
            customMetaDescription = `Shop genuine ${secondLastCategory} ${lastCategory} products online at ${websiteName}. MRO specialists for valves, controls, and industrial automation parts.`;
        } else if (isMainCategory) {
            customTitle = `Shop ${mainCategory} Products Online | ${websiteName}`;
            customTitle = finalMetaTitle || customTitle;
            customMetaDescription = `Browse our wide range of ${mainCategory} solutions at ${websiteName}. Trusted MRO supplier with fast shipping and technical support`;
            customMetaDescription = finalMetaDescription || customMetaDescription;
        } else if (isSubCategory) {
            customTitle = `Buy ${lastCategory} - ${mainCategory} | ${websiteName}`;
            customTitle = finalMetaTitle || customTitle;
            customMetaDescription = `Discover top-quality ${lastCategory} under ${mainCategory}. Shop online at ${websiteName} for MRO-grade parts with reliable delivery`;
            customMetaDescription = finalMetaDescription || customMetaDescription;
        } else {
            customTitle = `Shop ${lastCategory} - ${mainCategory} | ${websiteName}`;
            customTitle = finalMetaTitle || customTitle;
            customMetaDescription = `Explore ${lastCategory} solutions under ${mainCategory} at ${websiteName}. High-performance MRO parts with expert support & fast delivery`;
            customMetaDescription = finalMetaDescription || customMetaDescription;
        }
        const rawCanonicalPath = canonicalPath ? this.getCanonicalUrl(canonicalPath) : undefined;
        const canonicalBasePath = (rawCanonicalPath || location.pathname).split(/[?#]/)[0];
        const canonicalWithPage = `${canonicalBasePath}${this.getCanonicalQueryString()}`;
        setPageMetadata(
            {
                metaDescription: customMetaDescription,
                metaKeywords,
                openGraphImage: openGraphImage || primaryImagePath,
                openGraphTitle,
                openGraphUrl,
                title: customTitle,
                currentPath: location.pathname,
                canonicalPath: canonicalWithPage,
                alternateLanguageUrls: alternateLanguageUrls ?? undefined,
                websiteName,
                structuredPageData: websiteSettings.enableBreadcrumbStructuredData
                    ? prepareBreadcrumbStructuredPageData(this.props.breadcrumbLinks)
                    : undefined,
                jsonLdScriptId: getJsonLdScriptId(this.props.page.type),
            },
            websiteSettings,
        );
    }
    // Page-only query string for the canonical. Page 1 -> "", page N -> "?page=N".
// sort / filters / pageSize are intentionally excluded.
    private getCanonicalQueryString(): string {
        const { productsDataView, search } = this.props;
        const pagination = productsDataView.value ? productsDataView.pagination : undefined;

        let page = pagination?.currentPage;
        if (!page) {
            // data not loaded yet (e.g. SSR / first render): fall back to the URL
            const requested = Number(parseQueryString<{ page?: string }>(search).page);
            page = Number.isInteger(requested) && requested > 0 ? requested : 1;
        }

        return page > 1 ? `?page=${page}` : "";
    }

    setSearchPageMetadata(title: string) {
        const {
            websiteName,
            location,
            websiteSettings,
            fields,
            productsDataView,
            alternateLanguageUrls,
            canonicalUrl,
        } = this.props;
        const firstProduct = productsDataView.value && productsDataView.value[0];
        const { prevQueryString, currentQueryString, nextQueryString } = this.getQueryStrings();

        setPageMetadata(
            {
                title,
                currentPath: location.pathname,
                websiteName,
                openGraphImage:
                    fields.openGraphImage ||
                    firstProduct?.largeImagePath ||
                    firstProduct?.mediumImagePath ||
                    firstProduct?.smallImagePath,
                openGraphTitle: fields.openGraphTitle,
                openGraphUrl: location.pathname + location.search,
                alternateLanguageUrls,
                currentQueryString:this.getCanonicalQueryString(),
                prevQueryString,
                nextQueryString,
                structuredPageData: websiteSettings.enableBreadcrumbStructuredData
                    ? prepareBreadcrumbStructuredPageData(this.props.breadcrumbLinks)
                    : undefined,
                jsonLdScriptId: getJsonLdScriptId(this.props.page.type),
                canonicalPath: canonicalUrl && this.getCanonicalUrl(canonicalUrl),
            },
            websiteSettings,
        );
    }

    setProductListBreadcrumbs() {
        this.props.setBreadcrumbs({
            links: this.props.productListCatalogPage!.breadCrumbs!.map(o => ({ children: o.text, href: o.url })),
        });
    }

    setSearchBreadcrumbs() {
        this.props.setBreadcrumbs({
            links: [
                { children: translate("Home_Breadcrumb"), href: "/" },
                { children: `${translate("Search Results")}: ${this.props.query}` },
            ],
        });
    }

    render() {
        const windowIfDefined = typeof window === "undefined" ? null : window;
        if (
            this.props.productsDataView.value?.length &&
            (this.props.productsDataView.exactMatch ||
                (this.props.productsDataView.searchTermRedirectUrl &&
                    !windowIfDefined?.localStorage?.getItem("searchTermRedirectUrl")))
        ) {
            // prevent a flash of data when PLP immediately redirects to PDP
            return null;
        }
        const subcategoryItemListStructuredData = this.getSubcategoryItemListStructuredData();

        const productsItemListStructuredData = this.getProductsItemListStructuredData();

        const faqStructuredData = this.getFaqStructuredData();

        return (
            <>
                <Page {...styles.productListPageContainer}>
                    <CategoryContext.Provider value={this.props.category}>
                        <ProductListPageDataContext.Provider value={{ ref: this.afterFilters }}>
                            <Zone contentId={this.props.id} zoneName="Content" />
                            <Modals />
                            <ProductCompareFlyOut />
                        </ProductListPageDataContext.Provider>
                    </CategoryContext.Provider>
                </Page>
                {subcategoryItemListStructuredData && (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(subcategoryItemListStructuredData),
                        }}
                    />
                )}
                {productsItemListStructuredData && (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(productsItemListStructuredData),
                        }}
                    />
                )}
                {faqStructuredData && (
                    <script
                        type="application/ld+json"
                        // JSON-LD must be serialized into the script element for SSR.
                        // eslint-disable-next-line react/no-danger 
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(faqStructuredData),
                        }}
                    />
                )}
            </>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCallDeferredApi(withHistory(ProductListPage))),
    definition: {
        hasEditableUrlSegment: false,
        hasEditableTitle: false,
        supportsCategorySelection: true,
        pageType: "System",
        fieldDefinitions: [HideHeader, HideFooter, OpenGraphTitle, OpenGraphImage],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "ProductListPage" instead of this constant.
 */
export const ProductListPageContext = "ProductListPage";
